/**
 * The session ended elsewhere (Story 53, design r28): the user signed out
 * in another BlueSphere app, or was revoked. Identity's `POST /sign-out`
 * raises their `session_epoch` and emits `user.revoked`, so MDR's API
 * refuses the old token within seconds (task 218, 401 `revoked`), and the
 * gateway's authorizer once its deny set reloads (a bare 403).
 *
 * Any query or mutation refused that way ends this tab's session through
 * identity-client's `endSession`: MDR's tokens go and the gate shows "Your
 * BlueSphere session has ended", with no silent sign-in. The alert beacon's
 * 60-second poll (Story 25) carries this to an idle tab. A refused refresh
 * (`invalid_grant`) is the gate's own to handle.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { ApiError } from '@bluesphere/api-client';
import { endSession } from '@bluesphere/identity-client';

/** True for the API's `revoked`, and for the authorizer's refusal (no code). */
export function isSessionRefused(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 401 && err.code === 'revoked') return true;
  // API Gateway's own 403 from a REQUEST authorizer has no `error` field,
  // which the client fills in as `http_403`; the API's refusals carry theirs.
  return err.status === 403 && err.code === 'http_403';
}

export function SessionEndWatcher() {
  const auth = useAuth();
  const client = useQueryClient();

  useEffect(() => {
    let ending = false;
    const end = (err: unknown) => {
      if (ending || !isSessionRefused(err)) return;
      ending = true;
      void endSession(() => auth.removeUser());
    };
    const offQueries = client.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') end(event.action.error);
    });
    const offMutations = client.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') end(event.action.error);
    });
    return () => {
      offQueries();
      offMutations();
    };
  }, [auth, client]);

  return null;
}
